import { APP_BASE_HREF } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi
} from '@angular/common/http';
import {
    ApplicationConfig,
    importProvidersFrom,
    isDevMode,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
    PreloadAllModules,
    provideRouter,
    withPreloading,
} from '@angular/router';
import { provideEnvironment } from '@embrace-it/planning-portal-library';
import { provideCore, provideTranslationsForShell } from '@embraceit/angularcore';
import { provideTolgeeForStandalone } from '@embraceit/angularcore/tms/tolgee';

import { EnhancedDialogService } from '@embraceit/coreui-shared';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import {
    NavigationActionTiming,
    NgxsRouterPluginModule,
} from '@ngxs/router-plugin';
import { NgxsStoragePluginModule, StorageOption } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { NgxsWebSocketPluginModule } from '@ngxs/websocket-plugin';
import Aura from '@primeng/themes/aura';
import { provideUserIdleConfig } from 'angular-user-idle';
import { provideRepositories } from 'planning-portal-library/src/lib/data/data.providers';
import { BaseState } from 'planning-portal-library/src/lib/presentation/base/state/base.state';
import {
    HttpClientIdInterceptor,
    HttpDefaultInterceptor,
    HttpDistributionLaneInterceptor,
    HttpExternalUrlCleanupInterceptor,
    HttpSuscriptionKeyInterceptor,
    HttpTenantInterceptor,
    HttpTokenInterceptor,
} from 'planning-portal-library/src/lib/presentation/base/utils';
import { CommittedOrdersState } from 'planning-portal-library/src/lib/presentation/committed-order/state/committed-orders.state';
import { NewEditMasterPlansState } from 'planning-portal-library/src/lib/presentation/mater-plans/new-edit-master-plan/state/new-edit-master-plan.state';
import { MasterPlansState } from 'planning-portal-library/src/lib/presentation/mater-plans/state/master-plan.state';
import { SchedulerState } from 'planning-portal-library/src/lib/presentation/schedular/state/scheduler.state';
import { PlanningPortalGlobalState } from 'planning-portal-library/src/lib/presentation/state/planning-portal-global-state/planning-portal-global.state';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { APP_ROUTES } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom([
      // NGXS Configurations
      NgxsModule.forRoot([], {
        developmentMode: !isDevMode(),
      }),
      NgxsStoragePluginModule.forRoot({
        storage: StorageOption.LocalStorage,
        keys: [
          PlanningPortalGlobalState,
          NewEditMasterPlansState,
          MasterPlansState,
          CommittedOrdersState,
          SchedulerState,
          BaseState
        ],
      }),
      // NgxsLoggerPluginModule.forRoot({ disabled: isProduction }),
      NgxsReduxDevtoolsPluginModule.forRoot({ disabled: !isDevMode() }),
    
      NgxsWebSocketPluginModule.forRoot(),
      NgxsModule.forFeature([PlanningPortalGlobalState]),
      NgxsRouterPluginModule.forRoot({
        navigationActionTiming: NavigationActionTiming.PostActivation,
      }),
    ]),
    { provide: APP_BASE_HREF, useValue: '' },
    ...provideRepositories(),
    provideRouter(APP_ROUTES, withPreloading(PreloadAllModules)),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    Title,
    MessageService,
    DialogService,
    ConfirmationService,
    EnhancedDialogService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpTenantInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpDefaultInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpClientIdInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpDistributionLaneInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpSuscriptionKeyInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpExternalUrlCleanupInterceptor,
      multi: true,
    },
    provideEnvironment(environment),
    provideTranslationsForShell((lang) => import(`../assets/i18n/${lang}.json`)),
    provideUserIdleConfig({ idle: 900, timeout: 1, ping: 100 }),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark-mode',
          cssLayer: {
            name: 'primeng',
          },
        },
      },
    }),
    provideCore({
      mode: 'standalone',
    }),
    provideTolgeeForStandalone({
      translations: {
        languages: ['en'],
        defaultLanguage: 'en',
        fallbackNs: ['COMMON'],
        defaultNs: 'PP',
        tenantNs: 'DAO',
        ...environment.tolgee,
      },
      enableDevTools: isDevMode(),
    }),
  ],
};
